import { useCallback, useEffect, useState } from 'react';
import { Smartphone } from 'lucide-react';
import { api, post } from '../lib/api.js';
import { errorMessage } from '../lib/passkey.js';
import { Alert, fmtDate } from '../lib/ui.js';
import { t } from '../i18n/index.js';

type VapidInfo = { public_key: string | null; push_configured: boolean; email_configured: boolean };
export type PushDevice = {
  id: string;
  label: string;
  current: boolean;
  enabled_at: string;
  disabled_at: string | null;
  disabled_reason: 'user' | 'expired' | null;
  last_result: 'sent' | 'failed' | 'expired' | null;
  last_result_at: string | null;
  last_error: string | null;
};
type Status = { active: number; devices: PushDevice[] };

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * "Enable notifications" for the signed-in admin: registers a Web Push subscription for this
 * device and lists every device the server knows about with its last delivery result. "On for
 * this device" is true only when the browser's subscription is an active row on the server.
 */
export function NotificationsControl() {
  const supported =
    'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  const [info, setInfo] = useState<VapidInfo | null>(null);
  const [status, setStatus] = useState<Status | null>(null);
  const [browserSub, setBrowserSub] = useState<PushSubscription | null>(null);
  const [permission, setPermission] = useState<NotificationPermission>(
    supported ? Notification.permission : 'denied',
  );
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'error' | 'success' | 'info'; text: string } | null>(null);

  const refresh = useCallback(async () => {
    try {
      let sub: PushSubscription | null = null;
      if (supported) {
        const reg = await navigator.serviceWorker.ready;
        sub = await reg.pushManager.getSubscription();
      }
      setBrowserSub(sub);
      const q = sub ? `?endpoint=${encodeURIComponent(sub.endpoint)}` : '';
      const [i, s] = await Promise.all([
        api<VapidInfo>('/notifications/vapid-key'),
        api<Status>(`/notifications/status${q}`),
      ]);
      setInfo(i);
      setStatus(s);
    } catch (err) {
      setMsg({ kind: 'error', text: errorMessage(err) });
    }
  }, [supported]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const thisDevice = status?.devices.find((d) => d.current) ?? null;
  const onHere = !!browserSub && !!thisDevice && !thisDevice.disabled_at;
  const expiredHere = !!thisDevice?.disabled_at && thisDevice.disabled_reason === 'expired';

  const enable = async () => {
    if (!info?.public_key) return;
    setBusy(true);
    setMsg(null);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') return;
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      // An expired subscription must be replaced, not re-registered.
      if (sub && expiredHere) {
        await sub.unsubscribe();
        sub = null;
      }
      sub ??= await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(info.public_key),
      });
      await post('/notifications/subscribe', sub.toJSON());
      await refresh();
      setMsg({ kind: 'success', text: t('notif_enabled_now') });
    } catch (err) {
      setMsg({ kind: 'error', text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api('/notifications/subscribe', {
          method: 'DELETE',
          body: JSON.stringify({ endpoint: sub.endpoint }),
        });
        await sub.unsubscribe();
      }
      await refresh();
    } catch (err) {
      setMsg({ kind: 'error', text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  const lastDelivery = (d: PushDevice) => {
    if (d.disabled_at && d.disabled_reason === 'expired')
      return (
        <span className="pill grey">
          {t('notif_last_expired', { time: fmtDate(d.disabled_at) })}
        </span>
      );
    if (d.disabled_at)
      return (
        <span className="pill grey">
          {t('notif_disabled_user', { time: fmtDate(d.disabled_at) })}
        </span>
      );
    if (d.last_result === 'sent')
      return (
        <span className="pill green">
          {t('notif_last_sent', { time: fmtDate(d.last_result_at) })}
        </span>
      );
    if (d.last_result === 'failed')
      return (
        <span className="pill red" title={d.last_error ?? ''}>
          {t('notif_last_failed', { time: fmtDate(d.last_result_at) })}
        </span>
      );
    return <span className="muted">{t('notif_never')}</span>;
  };

  return (
    <section className="card">
      <h3>{t('notif_title')}</h3>
      <p>{t('notif_intro')}</p>
      {msg && <Alert kind={msg.kind}>{msg.text}</Alert>}
      {info && !info.push_configured && <Alert kind="info">{t('notif_push_unconfigured')}</Alert>}
      {info && !info.email_configured && <Alert kind="info">{t('notif_email_unconfigured')}</Alert>}
      {!supported ? (
        <p className="muted">{t('notif_unsupported')}</p>
      ) : permission === 'denied' ? (
        <Alert>{t('notif_blocked')}</Alert>
      ) : onHere ? (
        <div className="row">
          <span className="ok">{t('notif_enabled')}</span>
          <button type="button" className="small secondary" onClick={disable} disabled={busy}>
            {t('notif_disable')}
          </button>
        </div>
      ) : (
        <div className="stack-sm">
          {expiredHere && <Alert kind="info">{t('notif_expired_here')}</Alert>}
          <button type="button" onClick={enable} disabled={busy || !info?.push_configured}>
            {t('notif_enable')}
          </button>
        </div>
      )}
      {status && (
        <p className="muted" data-testid="notif-count">
          {status.active === 0
            ? t('notif_devices_none')
            : status.active === 1
              ? t('notif_devices_one')
              : t('notif_devices', { n: status.active })}
        </p>
      )}
      {status && status.devices.length > 0 && (
        <div className="table-wrap">
          <table className="devices" data-testid="notif-devices">
            <thead>
              <tr>
                <th>{t('notif_col_device')}</th>
                <th>{t('notif_col_enabled')}</th>
                <th>{t('notif_col_last')}</th>
              </tr>
            </thead>
            <tbody>
              {status.devices.map((d) => (
                <tr key={d.id} className={d.disabled_at ? 'muted' : ''}>
                  <td>
                    <span className="row">
                      <Smartphone size={14} aria-hidden="true" />
                      {d.label}
                      {d.current && <span className="pill teal">{t('notif_this_device')}</span>}
                    </span>
                  </td>
                  <td className="nowrap">{fmtDate(d.enabled_at)}</td>
                  <td>{lastDelivery(d)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
